import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CLEANER_PROPERTIES_QUERY_KEY, CLEANER_SECTIONS_QUERY_KEY, CLEANER_TASKS_QUERY_KEY } from "@/lib/cleanerQueryKeys";
import {
  GPS_POLL_INTERVAL_MS,
  haversineDistanceMeters,
  isBeyondAutoCheckoutDistance,
  isStaleWorkSession,
} from "@/lib/geoDistance";
import {
  closeStaleWorkSessions,
  closeWorkSession,
  fetchOpenWorkSessions,
  type OpenWorkSession,
} from "@/lib/workSessions";
import { setActiveWorkSession } from "@/lib/cleanerWorkSession";

function isDevBypass(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
}

function readCurrentPosition(): Promise<GeolocationPosition | null> {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      () => resolve(null),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
    );
  });
}

export function useWorkSessionGuard(params: {
  enabled: boolean;
  userId: string | null;
  orgId: string | null;
  gpsBypass?: boolean;
}) {
  const { enabled, userId, orgId, gpsBypass = false } = params;
  const queryClient = useQueryClient();
  const [openSessions, setOpenSessions] = useState<OpenWorkSession[]>([]);
  const [resumeSession, setResumeSession] = useState<OpenWorkSession | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const pollTimerRef = useRef<number | null>(null);
  const hiddenAtRef = useRef<number | null>(null);
  const didInitialInspectRef = useRef(false);

  const refreshOpenSessions = useCallback(async () => {
    if (!enabled || !userId || !orgId) {
      setOpenSessions([]);
      return [];
    }
    try {
      await closeStaleWorkSessions();
      const sessions = await fetchOpenWorkSessions(userId, orgId);
      setOpenSessions(sessions);
      for (const session of sessions) {
        setActiveWorkSession(session.location_id, true);
      }
      return sessions;
    } catch (error) {
      console.warn("[useWorkSessionGuard] refresh:", error);
      return [];
    }
  }, [enabled, orgId, userId]);

  const autoClose = useCallback(
    async (
      session: OpenWorkSession,
      reason: "geofence" | "stale_timeout",
      coords?: { lat: number; lng: number; distance: number },
    ) => {
      try {
        await closeWorkSession({
          sessionId: session.id,
          locationId: session.location_id,
          reason,
          latitude: coords?.lat ?? null,
          longitude: coords?.lng ?? null,
          distanceMeters: coords?.distance ?? null,
          userId: session.user_id,
          taskId: session.task_id,
        });
        setActiveWorkSession(session.location_id, false);
        setOpenSessions((prev) => prev.filter((item) => item.id !== session.id));
        setResumeSession((current) => (current?.id === session.id ? null : current));
        void queryClient.invalidateQueries({ queryKey: [CLEANER_PROPERTIES_QUERY_KEY] });
        void queryClient.invalidateQueries({ queryKey: [CLEANER_SECTIONS_QUERY_KEY] });
        void queryClient.invalidateQueries({ queryKey: [CLEANER_TASKS_QUERY_KEY] });
        if (reason === "geofence") {
          toast.warning(
            `Automatyczne wylogowanie: jesteś ponad ${session.auto_checkout_distance_meters ?? 400} m od obiektu. Koordynator zobaczy to w rejestrze.`,
          );
        } else {
          toast.warning("Sesja pracy zamknięta automatycznie po 8 godzinach. Wpis oznaczono do weryfikacji.");
        }
      } catch (error) {
        console.error("[useWorkSessionGuard] autoClose:", error);
      }
    },
    [queryClient],
  );

  const evaluatePosition = useCallback(
    (lat: number, lng: number) => {
      for (const session of openSessions) {
        if (session.location_lat == null || session.location_lng == null) continue;
        const distance = haversineDistanceMeters(session.location_lat, session.location_lng, lat, lng);
        const threshold = session.auto_checkout_distance_meters ?? 400;
        if (isBeyondAutoCheckoutDistance(distance, threshold)) {
          void autoClose(session, "geofence", { lat, lng, distance: Math.round(distance) });
        }
      }
    },
    [autoClose, openSessions],
  );

  const inspectOnResume = useCallback(async (opts?: { promptIfOnSite?: boolean }) => {
    if (!enabled || !userId || !orgId) {
      await refreshOpenSessions();
      return;
    }
    const sessions = await refreshOpenSessions();
    if (sessions.length === 0) return;
    if (gpsBypass || isDevBypass()) return;

    const position = await readCurrentPosition();
    for (const session of sessions) {
      if (isStaleWorkSession(session.started_at)) {
        await autoClose(session, "stale_timeout");
        continue;
      }
      if (position && session.location_lat != null && session.location_lng != null) {
        const distance = haversineDistanceMeters(
          session.location_lat,
          session.location_lng,
          position.coords.latitude,
          position.coords.longitude,
        );
        const threshold = session.auto_checkout_distance_meters ?? 400;
        if (isBeyondAutoCheckoutDistance(distance, threshold)) {
          await autoClose(session, "geofence", {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            distance: Math.round(distance),
          });
          continue;
        }
      }
      if (opts?.promptIfOnSite) {
        setResumeSession(session);
      }
    }
  }, [autoClose, enabled, gpsBypass, orgId, refreshOpenSessions, userId]);

  const continueSession = useCallback(() => {
    setResumeSession(null);
  }, []);

  const endSessionFromPrompt = useCallback(async () => {
    if (!resumeSession) return;
    setIsResolving(true);
    try {
      await closeWorkSession({
        sessionId: resumeSession.id,
        locationId: resumeSession.location_id,
        reason: "resume_prompt",
        userId: resumeSession.user_id,
        taskId: resumeSession.task_id,
      });
      setActiveWorkSession(resumeSession.location_id, false);
      setOpenSessions((prev) => prev.filter((item) => item.id !== resumeSession.id));
      setResumeSession(null);
      toast.success("Sesja pracy zakończona.");
    } catch (error) {
      console.error("[useWorkSessionGuard] endSessionFromPrompt:", error);
      toast.error("Nie udało się zakończyć sesji.");
    } finally {
      setIsResolving(false);
    }
  }, [resumeSession]);

  useEffect(() => {
    if (!enabled) return;
    if (didInitialInspectRef.current) return;
    didInitialInspectRef.current = true;
    void inspectOnResume({ promptIfOnSite: false });
  }, [enabled, inspectOnResume]);

  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => {
      if (document.visibilityState === "hidden") {
        hiddenAtRef.current = Date.now();
        return;
      }
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      const awayMs = hiddenAt ? Date.now() - hiddenAt : 0;
      void inspectOnResume({ promptIfOnSite: awayMs >= 60_000 });
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, inspectOnResume]);

  useEffect(() => {
    const stopPolling = () => {
      if (pollTimerRef.current != null) {
        window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    if (!enabled || gpsBypass || isDevBypass() || openSessions.length === 0) {
      stopPolling();
      return;
    }
    if (!navigator.geolocation) return;

    const pollGps = () => {
      if (document.visibilityState !== "visible") return;
      void readCurrentPosition().then((position) => {
        if (!position) return;
        evaluatePosition(position.coords.latitude, position.coords.longitude);
      });
    };

    pollGps();
    pollTimerRef.current = window.setInterval(pollGps, GPS_POLL_INTERVAL_MS);

    return stopPolling;
  }, [enabled, evaluatePosition, gpsBypass, openSessions.length]);

  return {
    openSessions,
    resumeSession,
    isResolving,
    continueSession,
    endSessionFromPrompt,
    refreshOpenSessions,
  };
}
